import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download } from 'lucide-react';
import { decideProposal, getProposal } from '@mkcore/portal/schema';
import { Badge, Button, Card, Checkbox, ErrorState, Field, Input, Skeleton, Textarea } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { Markdown } from '@/components/markdown';
import { proposalTone } from '@/features/portal/proposals';

/** A sent proposal (CO-0663): its lines, the choice of options and alternatives, and the decision of the client. */
export const Route = createFileRoute('/portal/_client/proposals/$id')({
  component: Proposal,
});

function Proposal() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['portal', 'proposal', id], queryFn: () => call(getProposal, { id }) });
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = q.data;
  const selected = useMemo(() => chosen ?? new Set((p?.lines ?? []).filter((l) => l.selected).map((l) => l.id)), [chosen, p]);
  if (q.isError) return <ErrorState title={t('portal.proposals.notFound')} description={message(q.error)} />;
  if (!p) return <Skeleton className="h-64" />;
  const open = p.state === 'open';
  const counts = (l: (typeof p.lines)[number]) => l.kind === 'item' || selected.has(l.id);
  // An estimate before VAT; the exact totals with VAT come back after the decision.
  const subtotal = p.lines.filter(counts).reduce((n, l) => n + l.amountCents, 0);
  const vat = p.lines.filter(counts).reduce((n, l) => n + Math.round((l.amountCents * l.vatRateBp) / 10_000), 0);
  const toggle = (l: (typeof p.lines)[number], on: boolean) => {
    const next = new Set(selected);
    if (l.kind === 'alternative') for (const x of p.lines) if (x.groupKey === l.groupKey) next.delete(x.id);
    if (on) next.add(l.id);
    else next.delete(l.id);
    setChosen(next);
  };
  const decide = async (decision: 'accepted' | 'rejected') => {
    setBusy(true);
    setError(null);
    try {
      const after = await call(decideProposal, {
        id,
        decision,
        signedName: name.trim(),
        ...(decision === 'accepted' && { selectedLineIds: p.lines.filter((l) => l.kind !== 'item' && selected.has(l.id)).map((l) => l.id) }),
        ...(note.trim() && { note: note.trim() }),
      });
      qc.setQueryData(['portal', 'proposal', id], after);
      await qc.invalidateQueries({ queryKey: ['portal', 'proposals'] });
      await qc.invalidateQueries({ queryKey: ['portal', 'home'] });
    } catch (err) {
      setError(message(err));
    }
    setBusy(false);
  };
  const groups = [...new Set(p.lines.filter((l) => l.kind === 'alternative').map((l) => l.groupKey!))];
  const groupsOk = groups.every((g) => p.lines.some((l) => l.groupKey === g && selected.has(l.id)));
  return (
    <>
      <Link to="/portal/proposals" className="flex w-fit items-center gap-1 py-1 text-sm text-fg-2 hover:underline">
        <ArrowLeft className="size-4" aria-hidden /> {t('portal.modules.proposals')}
      </Link>
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">
            <span className="text-fg-3 tabular">{p.number}</span> {p.title}
          </h1>
          <Badge tone={proposalTone[p.state]}>{t(`portal.proposals.state.${p.state}`)}</Badge>
        </div>
        <p className="flex flex-wrap gap-3 text-sm text-fg-2">
          {p.validUntil && open && <span>{t('portal.proposals.validUntil', { date: formatDate(p.validUntil) })}</span>}
          {p.hasPdf && (
            <a href={`/api/v1/portal/proposals/${p.id}/pdf?download=1`} className="flex items-center gap-1 underline">
              <Download className="size-4" aria-hidden /> {t('portal.proposals.pdf')}
            </a>
          )}
        </p>
      </div>
      {p.intro && (
        <Card className="p-4">
          <Markdown text={p.intro} />
        </Card>
      )}
      {p.scope && (
        <Card className="grid gap-2 p-4">
          <h2 className="text-lg font-bold">{t('portal.proposals.scope')}</h2>
          <Markdown text={p.scope} />
        </Card>
      )}
      <Card className="grid gap-3 p-4">
        <h2 className="text-lg font-bold">{t('portal.proposals.lines')}</h2>
        <ul className="grid gap-2">
          {p.lines.map((l) => (
            <li key={l.id} className="flex items-start justify-between gap-3 border-b border-line-subtle pb-2 last:border-0">
              <div className="flex items-start gap-2">
                {l.kind === 'option' &&
                  (open ? <Checkbox className="mt-0.5" aria-label={t('portal.proposals.chooseOption', { name: l.description })} checked={selected.has(l.id)} onCheckedChange={(v) => toggle(l, v === true)} /> : null)}
                {l.kind === 'alternative' &&
                  (open ? <input type="radio" className="mt-1" name={`group-${l.groupKey}`} aria-label={t('portal.proposals.chooseAlternative', { name: l.description })} checked={selected.has(l.id)} onChange={() => toggle(l, true)} /> : null)}
                <div>
                  <span className={counts(l) ? '' : 'text-fg-3 line-through'}>{l.description}</span>
                  <span className="block text-xs text-fg-3">
                    {[l.kind !== 'item' && t(`portal.proposals.kind.${l.kind}`), l.phase, `${l.quantity} × ${formatMoney(l.unitPriceCents, p.currency)}`].filter(Boolean).join(' · ')}
                  </span>
                </div>
              </div>
              <span className="tabular">{formatMoney(l.amountCents, p.currency)}</span>
            </li>
          ))}
        </ul>
        <dl className="grid justify-end gap-1 text-sm tabular">
          <div className="flex justify-between gap-6">
            <dt>{t('portal.proposals.subtotal')}</dt>
            <dd>{formatMoney(open ? subtotal : p.subtotalCents, p.currency)}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt>{t('portal.proposals.vat')}</dt>
            <dd>{formatMoney(open ? vat : p.vatCents, p.currency)}</dd>
          </div>
          <div className="flex justify-between gap-6 font-bold">
            <dt>{t('portal.proposals.total')}</dt>
            <dd>{formatMoney(open ? subtotal + vat : p.totalCents, p.currency)}</dd>
          </div>
        </dl>
      </Card>
      {p.terms && (
        <Card className="grid gap-2 p-4">
          <h2 className="text-lg font-bold">{t('portal.proposals.terms')}</h2>
          <Markdown text={p.terms} />
        </Card>
      )}
      {p.decision ? (
        <p role="status" className="rounded-md bg-sunken px-3 py-2 text-sm">
          {t(`portal.proposals.decided.${p.decision.decision}`, { name: p.decision.byName ?? '', at: formatDateTime(p.decision.at) })}
        </p>
      ) : open ? (
        <Card className="grid gap-3 p-4" role="region" aria-labelledby="decide-title">
          <h2 id="decide-title" className="text-lg font-bold">
            {t('portal.proposals.decide')}
          </h2>
          <Field label={t('portal.proposals.signedName')} hint={t('portal.proposals.signedNameHint')}>
            {(f) => <Input {...f} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          {!declining ? (
            <>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox className="mt-0.5" checked={agree} onCheckedChange={(v) => setAgree(v === true)} /> {t('portal.proposals.agree')}
              </label>
              {!groupsOk && <p className="text-sm text-danger">{t('portal.proposals.chooseEachGroup')}</p>}
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button loading={busy} disabled={name.trim().length < 2 || !agree || !groupsOk} onClick={() => void decide('accepted')}>
                  {t('portal.proposals.accept')}
                </Button>
                <Button variant="ghost" onClick={() => setDeclining(true)}>
                  {t('portal.proposals.declineInstead')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Field label={t('portal.proposals.note')}>{(f) => <Textarea {...f} rows={3} value={note} autoFocus onChange={(e) => setNote(e.target.value)} />}</Field>
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" loading={busy} disabled={name.trim().length < 2} onClick={() => void decide('rejected')}>
                  {t('portal.proposals.decline')}
                </Button>
                <Button variant="ghost" onClick={() => setDeclining(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </>
          )}
        </Card>
      ) : null}
    </>
  );
}
